import React, { useContext, useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { AppContext, DispatchContext } from "../AppContext";
import Paper from '@material-ui/core/Paper';
import { makeStyles } from "@material-ui/styles";
import * as actions from '../actions/actions';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { Typography, IconButton } from '@material-ui/core';
import { config, formatPercent, blendColors, resolveNotificationMessage } from '../helpers/constants';
import IconLabel from './IconLabel';
import Service from "../services/service";
import CircularLoading from "./CircularLoading";

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    padding: 8,
    lineHeight: '18px',
    height: "100%",
    position: "relative"
  },
  tooltip: {
    maxWidth: "480px !important",
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  paper: {
    display: "flex",
    flex: "1 1 0",
    minHeight: 8,
    padding: 10,
    boxShadow: "0px 0px 2px 2px rgba(0, 0, 0, 0.1)",
    zIndex: 2,
  },
  content: {
    display: 'flow-root',
    flexDirection: "row",
    flex: "1 1 0",
    minHeight: 0,
    overflowX: "hidden",
    overflowY: "auto",
    height: "100%",
    wordBreak: "break-word",
    whiteSpace: (state) => { return (state.ui.showLinebreaks) ? "pre-line" : ""; }
  },
  span: {
    position: "relative",
    cursor: "pointer",
    border: "1px solid white",
    zIndex: "10",
  },
  spanIcon: {
    position: "absolute",
    top: 0,
    right: 0,
    opacity: 0.2,
    "&:hover": {
      opacity: 1
    }
  },
  accepted: {
    backgroundImage: "url(/gfx/accepted-stripe.png)", // created with http://www.patternify.com/
  },
  mainDiv: {
    margin: 0,
    padding: 0,
    height: "30px",
    position: "relative",
  },
  centerDiv: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "white",
  },
  divider: {
    width: "75%",
    height: "2px",
    backgroundColor: theme.palette.secondary.main,
    opacity: 0.2,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)"
  },
  common_div: {
    overflow: "hidden",
    width: "100%",
  },
  contentLeft: {
    overflowY: "scroll",
    overflowX: "hidden",
    height: "100%",
  },
  fullWidth: {
    width: "100%",
  },
  displayNone: {
    display: 'none',
  }
}));


export default function SourceData(props) {
  const dispatch = useContext(DispatchContext);
  const state = useContext(AppContext);
  const classes = useStyles(state);
  const { refData, scrollToContent, hoverToContent, hoverToOutContent, handleOpen } = props;
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const [loading, setLoading] = useState(false);
  const s = state.strings;

  // Register key down handler for Ctrl key handling
  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress, false);
    document.addEventListener("keyup", handleKeyUp, false);
    return () => {
      document.removeEventListener("keydown", handleKeyPress, false);
      document.removeEventListener("keyup", handleKeyUp, false);
    };
  }, []);

  // load source markup for single source view if active source has changed
  useEffect(() => {
    if ((state.activeReport) && (state.activeReport.Status == "Ok") && (state.ui.sourcesView == 'sources') && (state.activeSource.Id)) {
      setLoading(true);
      Service.getSourceMarkup(state.activeSource.Id, state.ui.permaLinkId).then(res => {
        setLoading(false);
        if (res.success && res.data) {
          dispatch(actions.setSourceMarkup(res.data));
        } else {
          dispatch(actions.openToast({ type: "error", message: "Comparsion Data not Found!", status: true }));
        }
      });
    }
  }, [state.activeSource, state.ui.reportDisplayMode, state.activeReport.Status]);

  useEffect(() => {
    if ((state.activeReport) && (state.activeReport.Status == "Ok") && (state.activeReport.TextOverlayExists)) {
      setLoading(true);
      Service.getTextOverlay(state.activeReportId, state.ui.statisticsType, state.ui.permaLinkId).then(res => {
        setLoading(false);
        if (res.success) {
          dispatch(actions.set_text_overlay(res.data));
        }
        else {
          dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
        }
      });
    }
}, [state.ui.statisticsType, state.activeReportId, state.ui.permaLinkId, state.activeReport.Status]);



const handleExpandTextButton = () => {
  dispatch(actions.setUI({ showTextWithoutMarkup: true }));
}

const activateSource = (source, plagSource) => {
  scrollToContent(source.SId)
  const storeSource = state.activeSource;
  if (plagSource.Id != storeSource.Id) {
    dispatch(actions.setActiveSource(plagSource));
  }
  if (ctrlPressed) {
    handleOpen(true, plagSource.Id);
  }
}

// Hover In effect 
const activateHover = (source) => {
  hoverToContent('list' + source.SIdx);
  hoverToContent(source.SId);
}

// Hover Out effect 
const activateHoverOut = (source) => {
  hoverToOutContent('list' + source.SIdx);
  hoverToOutContent(source.SId);
}

// select all rows if Ctrl+A has been pressed
const handleKeyPress = (e) => {
  if (e.ctrlKey) {
    setCtrlPressed(true);
    return false;
  }

}

const handleKeyUp = (e) => {
  setCtrlPressed(false);
}

const getSourceTip = (source, plagSource) => {
  return (source.Sim ?
    `<b>${s.reportReview.sourceTipHeader.format(source.SIdx, plagSource.MW, formatPercent(plagSource.Percent), source.PIdx, plagSource.MP, formatPercent(source.Sim))}</b><br />${plagSource.Title}<br><i>${s.reportReview.sourceTooltipHint}</i>`
    :
    `<b>${s.reportReview.sourceTipHeaderNoSim.format(source.SIdx, plagSource.MW, formatPercent(plagSource.Percent), source.PIdx, plagSource.MP)}</b><br />${plagSource.Title}<br><i>${s.reportReview.sourceTooltipHint}</i>`
  );
}

const getStatsTip = (index) => {
  if (!state.textOverlay)
    return "";

  return (state.ui.statisticsMode == 'v') ?
    `${s.kiDetector[state.ui.textOverlay[state.ui.statisticsType].title]}: ${state.textOverlay[index][state.ui.statisticsMode].toPrecision(3)}`
    :
    `${s.kiDetector[state.ui.textOverlay[state.ui.statisticsType].title]}: ${state.textOverlay[index][state.ui.statisticsMode].toPrecision(3)}<br />${s.kiDetector.rawValue}: ${state.textOverlay[index]['v'].toPrecision(3)}`
}

const getStatsValues = () => state.ui.textOverlay[state.ui.statisticsType] ? state.ui.textOverlay[state.ui.statisticsType].values : state.ui.textOverlay['default'].values;
const getStatsColors = () => state.ui.textOverlay[state.ui.statisticsType] ? state.ui.textOverlay[state.ui.statisticsType].colors : state.ui.textOverlay['default'].colors;

//return (<div className={classes.root}>Source List Panel</div>);
return (
  <div className={classes.root}>

    <Paper elevation={3} className={classes.paper}>
      {state.activeReport && state.activeReport.Status == "Ok" && state.ui.sourcesView == 'sources' && state.ui.reportDisplayMode == 'source' &&
        <div className={classes.common_div}>
          <Grid container className={classes.contentLeft}>
            <CircularLoading visible={loading} />

            {!loading &&
              (state.sourceMarkup && state.sourceMarkup.Text2 ?
                <span>
                  {state.sourceMarkup.Text2.Markup.map((source, index) => {
                    if (source.PIdx == null) return source.T;
                    var color = state.ui.colors[source.PIdx % 10];
                    return (
                      <span key={index} style={{ backgroundColor: color }}>{source.T}</span>
                    );
                  })}
                </span> :
                <IconLabel centered label={s.reportReview.selectSource} />)
            }
          </Grid>
        </div>
      }

      {state.activeReport && state.activeReport.Status == "Ok" && state.ui.sourcesView == 'statistics' &&
        <>
          <Grid container className={classes.content}>
            <CircularLoading visible={loading} />
            {!loading && (state.textOverlay.length ?
                <span>
                  {state.textOverlay.map((phrase, index) => {
                    if (phrase[state.ui.statisticsMode] == null) return phrase.t;
                    var color = blendColors(phrase[state.ui.statisticsMode], getStatsColors(), getStatsValues());
                    return (
                      <span data-for="sourceTT" data-tip={getStatsTip(index)} key={index} style={{ backgroundColor: color }}>{phrase.t}</span>
                    );
                  })}
                </span> :
                <IconLabel centered label={"No Text Overlay"} />)
            }
          </Grid>
        </>
      }

      {state.activeReport && state.activeReport.Status == "Ok" && state.ui.sourcesView == 'sources' && state.ui.reportDisplayMode == 'merged' &&
        <>
          <Grid container className={classes.content}>
            <CircularLoading visible={!state.loadingSourceDetail } />
            {state.reportMarkup.length ?
              state.reportMarkup.map((source, index) => {
                var plagSource = state.sources.find(s => s && s.Id == source.SId);
                if ((!source.PIdx) || (!plagSource)) {
                  const sourcefString = (source.T) ? source.T.toString().split(" ") : "";
                  if (sourcefString.length > config.truncateChars && !state.ui.showTextWithoutMarkup) {
                    return (
                      <React.Fragment>
                        {sourcefString.slice(0, config.truncateChars / 2).join(" ") + "... "}
                        <div className={classes.mainDiv}>
                          <div className={classes.divider}></div>

                          <IconButton data-for="globalTT" data-tip={s.reportReview.showAllText} size="small" onClick={handleExpandTextButton} className={classes.centerDiv}>
                            <MoreHorizIcon color="secondary" />
                          </IconButton>

                        </div>
                        {"..." + sourcefString.slice(sourcefString.length - config.truncateChars / 2, sourcefString.length + 1).join(" ")}
                      </React.Fragment>
                    )
                  }
                  return source.T;
                } else {
                  return (
                    <span data-for="sourceTT" data-tip={getSourceTip(source, plagSource)}
                      ref={refData} id={source.SId + '_source' + '_phase_' + source.PIdx}
                      onClick={() => activateSource(source, plagSource)}
                      onMouseOver={() => activateHover(source)}
                      onMouseLeave={() => activateHoverOut(source)}
                      style={{ backgroundColor: state.ui.colors[source.SIdx % 10] }}
                      className={`${source.SId} ${(plagSource.Accepted == 1) ? classes.accepted : ''} ${classes.span} ${(state.ui.filterPIdx == 0 || state.ui.filterPIdx == source.SIdx) ? classes[source.SIdx] : ''}`}>
                      {source.T}
                    </span>
                  );
                }
              }) :
              (state.loadingSourceDetail && <IconLabel centered type="check" label={s.noRelevantSources} />)
            }
          </Grid>

        </>
      }
    </Paper>
  </div>
)
}
